import { CheckBox, LoadingSpinner } from '@idexx-laboratories/spot-react';
import classNames from 'classnames';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { ReactElement } from 'react';
import './Grid.scss';

export interface GridColDef<T> {
  headerName: string;
  field: (string & keyof T) | string;
  renderer?: GridRenderer<T>;
  renderValue?: (props: GridRendererProps<T>) => string;
  checked?: boolean;
  setChecked?: (newValue: boolean) => void;
  sortable?: boolean;
}

export interface SortingObservable<T> {
  field?: keyof T | string;
  order?: 'ASC' | 'DESC';
}

type CellProps<T> = { colDef: GridColDef<T>; disabled?: boolean };

const getRowValue = <T,>(cell: GridColDef<T>, row: T) => {
  const val = cell.field
    .split('.')
    .reduce((prev: any, curr: any) => (prev ? prev[curr] : null), row || '');
  return val || val === 0 ? val : '';
};

const CellHeader = <T,>(props: CellProps<T>) => {
  const { colDef, disabled } = props;
  return colDef.checked !== undefined && colDef.setChecked !== undefined ? (
    <CheckBox
      id='grid-header-checkbox'
      label={colDef.headerName}
      data-testid='grid-header-checkbox'
      disabled={disabled}
      checked={colDef.checked || false}
      onChange={() => colDef.setChecked?.(!colDef.checked)}
    />
  ) : (
    <>{colDef.headerName}</>
  );
};

export interface GridRendererProps<T> {
  row: T;
  index: number;
  tableId?: string;
  expandedRowIds?: string[];
}

export type GridRenderer<T> = (props: GridRendererProps<T>) => React.JSX.Element | null;

type GridProps<T extends Record<string, any>> = {
  id: string;
  columns: GridColDef<T>[];
  disabled?: boolean;
  loading?: boolean | ReactElement;
  zebraRows?: boolean;
  data: T[] | undefined;
  sorting?: SortingObservable<T>;
  onSortingChanged?: () => void;
  onRowClick?: (index: number) => void;
  subrowRenderer?: GridRenderer<T>;
  expandedRowIds?: any[];
  rowIdField?: keyof T;
  rowLoadingField?: keyof T;
  noRowsText?: string;
  className?: string;
};

const Grid = observer(<T extends Record<string, any>>(props: GridProps<T>) => {
  const {
    id,
    columns,
    loading,
    zebraRows,
    data,
    sorting,
    onSortingChanged,
    subrowRenderer,
    onRowClick,
    className,
    expandedRowIds,
    rowIdField,
    rowLoadingField,
    noRowsText,
    disabled,
  } = props;

  let curId = 1;
  const ids = new WeakMap<T>();

  const getKey = (row: T): string => {
    if (ids.has(row)) {
      return ids.get(row);
    }
    const id1 = String(curId++);
    ids.set(row, id1);
    return id1;
  };

  const switchSorting = (field: string) => {
    if (!sorting) return;
    runInAction(() => {
      if (sorting.field === field) {
        sorting.order = sorting.order === 'ASC' ? 'DESC' : 'ASC';
      } else {
        sorting.field = field;
        sorting.order = 'ASC';
      }
    });
    onSortingChanged?.();
  };

  return (
    <table
      id={id}
      className={classNames(
        'spot-data-table',
        'spot-data-table--small-spacing',
        'spot-data-table--sortable',
        className,
      )}
    >
      <colgroup>
        {columns.map((colDef, index) => (
          <col
            key={colDef.headerName || colDef.field}
            className={`spot-data-table__col spot-data-table__col--index-${index}`}
          />
        ))}
      </colgroup>
      <thead>
        <tr>
          {columns.map(colDef => (
            <th
              key={colDef.headerName || colDef.field}
              className={classNames({
                'spot-data-table__col--active': colDef.sortable && sorting?.field === colDef.field,
                'spot-data-table__col--sort-ascending':
                  colDef.sortable && sorting?.field === colDef.field && sorting.order === 'ASC',
                'spot-data-table__col--sort-descending':
                  colDef.sortable && sorting?.field === colDef.field && sorting.order === 'DESC',
                'spot-data-table__col--inactive':
                  colDef.sortable && sorting?.field !== colDef.field,
                'spot-data-table__col--sort-disabled': !colDef.sortable,
              })}
              onClick={() => colDef.sortable && switchSorting(colDef.field)}
              data-testid={`${id}-${colDef.headerName}-header`}
            >
              <CellHeader colDef={colDef} disabled={disabled} />
            </th>
          ))}
        </tr>
      </thead>
      {loading ? (
        <tbody>
          <tr>
            <td
              colSpan={columns.length}
              style={
                loading === true ? { textAlign: 'center', padding: '10px' } : { padding: '10px' }
              }
            >
              {loading === true ? <LoadingSpinner /> : loading}
            </td>
          </tr>
        </tbody>
      ) : (
        <tbody>
          {!data || data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className='no-data-row'>
                {noRowsText || 'No data'}
              </td>
            </tr>
          ) : (
            <>
              {data.map((row, index) => (
                <React.Fragment key={getKey(row)}>
                  <tr
                    className={classNames({
                      'hover-gray': !zebraRows,
                      'gray-row': zebraRows && index % 2 === 1,
                    })}
                    onClick={() => onRowClick?.(index)}
                    onKeyDown={() => {}}
                    data-testid={`${id}-${index}-row`}
                  >
                    {rowLoadingField && row[rowLoadingField] ? (
                      <td colSpan={columns.length} style={{ textAlign: 'center', padding: '6px' }}>
                        <LoadingSpinner />
                      </td>
                    ) : (
                      columns.map(colDef => (
                        <td
                          key={colDef.headerName || colDef.field}
                          data-testid={`${id}-${index}-${colDef.headerName}-cell`}
                        >
                          {colDef.renderer ? (
                            React.createElement(colDef.renderer, {
                              row,
                              index,
                              tableId: id,
                              expandedRowIds,
                            })
                          ) : colDef.renderValue ? (
                            <>{colDef.renderValue({ row, index, tableId: id, expandedRowIds })}</>
                          ) : (
                            getRowValue(colDef, row)
                          )}
                        </td>
                      ))
                    )}
                  </tr>
                  {subrowRenderer &&
                    expandedRowIds &&
                    rowIdField &&
                    expandedRowIds.includes(row[rowIdField]) &&
                    !(rowLoadingField && row[rowLoadingField]) && (
                      <tr className={classNames('subrow', { 'hover-gray': !zebraRows })}>
                        <td colSpan={columns.length}>
                          {React.createElement(subrowRenderer, { row, index, tableId: id })}
                        </td>
                      </tr>
                    )}
                </React.Fragment>
              ))}
            </>
          )}
        </tbody>
      )}
    </table>
  );
});

export default Grid;
